export interface CompareOption {
  id: string;
  name: string;
  shortName: string;
  pros: string[];
  cons: string[];
  bestFor: string;
  taxAdvantage: string;
  annualLimit: string;
  withdrawalCondition: string;
  color: string;
}

export interface Comparison {
  id: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  verdict: string;
  verdictDetail: string;
  options: [CompareOption, CompareOption];
  commonQuestion: string;
  comparisonTable: {
    rows: { label: string; a: string; b: string }[];
  };
  relatedLinks: { label: string; href: string }[];
}

export const COMPARISONS: Comparison[] = [
  {
    id: 'isa-vs-irp',
    title: 'ISA vs IRP — 어떤 계좌가 유리할까?',
    metaTitle: 'ISA vs IRP 비교 — 세제혜택·납입한도·인출 조건 총정리 | 노후든든',
    metaDescription: 'ISA와 IRP를 완전 비교합니다. 세제혜택, 납입 한도, 인출 조건, 투자 가능 상품을 한눈에 비교하고 내 상황에 맞는 계좌를 선택하세요.',
    verdict: '단기 목적 자금은 ISA, 노후 연금은 IRP — 둘 다 활용하는 것이 최적',
    verdictDetail: 'ISA는 3년 후 자유롭게 인출 가능하고 배당소득 비과세(200만 원) 혜택이 있어 중기 투자에 유리합니다. IRP는 55세 이후 연금 수령 시 3.3~5.5% 저율과세로 직장인 퇴직금 운용에 적합합니다. 납입 여유가 있다면 ISA 2,000만 원과 IRP 조합으로 세제 혜택을 넓히는 전략이 좋습니다.',
    commonQuestion: 'ISA vs IRP, 내 상황에서 어떤 계좌가 더 유리할까?',
    options: [
      {
        id: 'isa',
        name: '개인종합자산관리계좌 (ISA)',
        shortName: 'ISA',
        pros: ['비과세 혜택 200만 원(서민형 400만 원)', '3년 후 자유 인출', '다양한 상품 투자 가능', '만기 후 IRP 이전 가능'],
        cons: ['소득 없으면 가입 불가', '3년 의무 유지 필요', '소득공제 혜택 없음'],
        bestFor: '중기(3~5년) 투자 목적 직장인',
        taxAdvantage: '이자·배당 비과세 200만 원 (서민형 400만 원)',
        annualLimit: '2,000만 원 (5년간 최대 1억)',
        withdrawalCondition: '3년 후 자유 인출',
        color: '#3b82f6',
      },
      {
        id: 'irp',
        name: '개인형 퇴직연금 (IRP)',
        shortName: 'IRP',
        pros: ['연간 900만 원 세액공제 (13.2~16.5%)', '55세 이후 연금 수령 시 3.3~5.5% 저율과세', '퇴직금 이전 시 세금 이연'],
        cons: ['55세 이후 인출 원칙', '중도 해지 시 16.5% 기타소득세', '주식 직접 투자 불가'],
        bestFor: '노후 연금 목적, 퇴직금 운용',
        taxAdvantage: '연 최대 148.5만 원 세액공제',
        annualLimit: '연 1,800만 원 (DC형 합산)',
        withdrawalCondition: '55세 이후 연금 수령',
        color: '#10b981',
      },
    ],
    comparisonTable: {
      rows: [
        { label: '가입 대상', a: '만 19~64세 소득자', b: '소득 있는 누구나' },
        { label: '연간 한도', a: '2,000만 원', b: '1,800만 원' },
        { label: '세제 혜택', a: '이자·배당 비과세 200만 원', b: '납입액 세액공제 13.2~16.5%' },
        { label: '인출 조건', a: '3년 후 자유', b: '55세 이후 연금' },
        { label: '투자 상품', a: 'ETF·펀드·예금·ELS', b: 'ETF·펀드·예금 (주식 불가)' },
        { label: '중도 해지', a: '세금 납부 후 가능', b: '기타소득세 16.5%' },
      ],
    },
    relatedLinks: [
      { label: 'ISA·IRP 세제혜택 계산기', href: '/tax-benefit' },
      { label: '배당 ETF를 ISA로 운용하기', href: '/dividend' },
    ],
  },
  {
    id: 'schd-vs-jepi',
    title: 'SCHD vs JEPI — 어떤 배당 ETF가 유리할까?',
    metaTitle: 'SCHD vs JEPI 비교 — 배당성장형 vs 고배당 커버드콜 총정리 | 노후든든',
    metaDescription: 'SCHD와 JEPI를 완전 비교합니다. 배당수익률, 배당 성장률, 주가 상승 참여, 적합한 투자 시기를 한눈에 비교하세요.',
    verdict: '장기(20년+) 투자면 SCHD, 10년 내 인출 예정이라면 JEPI',
    verdictDetail: 'SCHD는 배당 성장률이 높아 20년 이상 장기 투자에서 원금 대비 배당수익률이 크게 올라갑니다. JEPI는 높은 월배당으로 즉각적인 현금 흐름을 만들기 좋지만 커버드콜 구조상 주가 상승 참여가 제한됩니다. 인출 시점과 현금 흐름 필요 시점을 기준으로 선택하는 것이 핵심입니다.',
    commonQuestion: 'SCHD와 JEPI 중 어떤 ETF가 내 상황에 맞을까?',
    options: [
      {
        id: 'schd',
        name: 'Schwab 미국 배당주 ETF (SCHD)',
        shortName: 'SCHD',
        pros: ['연 10% 수준 배당성장률', '주가 상승 완전 참여', '낮은 운용보수 0.06%', '배당 증가 기업 중심'],
        cons: ['초기 배당수익률 3.5%로 낮음', '해외 직투 필요', '분기 배당'],
        bestFor: '20년 이상 장기 배당성장 투자',
        taxAdvantage: '미국 원천징수세 15%',
        annualLimit: '없음',
        withdrawalCondition: '언제든 가능',
        color: '#3b82f6',
      },
      {
        id: 'jepi',
        name: 'JPMorgan 프리미엄 소득 ETF (JEPI)',
        shortName: 'JEPI',
        pros: ['연 7.5% 수준 고배당', '월배당으로 현금 흐름 즉각', '변동성 완충 효과'],
        cons: ['배당 성장률이 낮음', '커버드콜로 주가 상승 제한', '배당 금액이 월별로 변동'],
        bestFor: '10년 내 인출 예정 또는 은퇴 후 현금 흐름',
        taxAdvantage: '미국 원천징수세 15%',
        annualLimit: '없음',
        withdrawalCondition: '언제든 가능',
        color: '#f59e0b',
      },
    ],
    comparisonTable: {
      rows: [
        { label: '배당수익률', a: '3.5%', b: '7.5%' },
        { label: '배당 성장률', a: '연 10%', b: '연 1%' },
        { label: '주가 성장률', a: '연 10%', b: '연 4%' },
        { label: '배당 주기', a: '분기', b: '월' },
        { label: '운용보수', a: '0.06%', b: '0.35%' },
        { label: '20년 후 YOC', a: '약 22%', b: '약 9%' },
      ],
    },
    relatedLinks: [
      { label: '배당 ETF 계산기로 시뮬레이션', href: '/dividend' },
      { label: 'ISA 계좌로 배당세 절세하기', href: '/tax-benefit' },
    ],
  },
  {
    id: 'national-pension-vs-private',
    title: '국민연금 vs 개인연금 — 노후 준비는 무엇이 유리할까?',
    metaTitle: '국민연금 vs 개인연금 비교 — 노후 준비 전략 총정리 | 노후든든',
    metaDescription: '국민연금과 개인연금(IRP·연금저축)을 비교합니다. 수익률, 세제혜택, 수령 나이, 물가 연동 여부를 한눈에 비교하고 최적 조합을 찾으세요.',
    verdict: '국민연금이 기초, 개인연금으로 보완 — 두 가지 모두 필수',
    verdictDetail: '국민연금은 물가 연동과 종신 지급이라는 장점 덕분에 노후 소득의 기초 역할을 합니다. 다만 소득대체율이 충분하지 않을 수 있어 개인연금으로 추가 자산을 쌓아야 합니다. 국민연금과 IRP, 장기 투자 계좌를 조합한 3층 구조가 안정적인 노후 설계에 도움이 됩니다.',
    commonQuestion: '국민연금만으로 노후 준비가 될까? 개인연금이 필요한 이유는?',
    options: [
      {
        id: 'national-pension',
        name: '국민연금',
        shortName: '국민연금',
        pros: ['물가 연동으로 실질 가치 유지', '종신 지급', '유족연금·장애연금 부가 혜택', '가입 강제성으로 누락 방지'],
        cons: ['소득대체율 40% 수준으로 부족', '의무 가입으로 자율성 낮음', '65세 이후 수령'],
        bestFor: '기초 노후 소득 확보',
        taxAdvantage: '보험료 소득공제',
        annualLimit: '소득의 9%',
        withdrawalCondition: '65세 이후 (조기수령 시 감액)',
        color: '#6366f1',
      },
      {
        id: 'private-pension',
        name: '개인연금 (IRP·연금저축)',
        shortName: '개인연금',
        pros: ['연 최대 148.5만 원 세액공제', '55세부터 수령 가능', '투자 상품 선택 가능', '수령 기간 설계 가능'],
        cons: ['물가 연동 없음', '투자 손실 가능성', '55세 전 해지 시 불이익'],
        bestFor: '국민연금 보완 및 세금 절감',
        taxAdvantage: '연 최대 148.5만 원 세액공제',
        annualLimit: '연 1,800만 원',
        withdrawalCondition: '55세 이후 연금 수령',
        color: '#10b981',
      },
    ],
    comparisonTable: {
      rows: [
        { label: '가입 방식', a: '의무 가입', b: '자율 가입' },
        { label: '소득대체율', a: '약 40%', b: '납입액에 따라 다름' },
        { label: '물가 연동', a: 'O (CPI 연동)', b: 'X' },
        { label: '수령 나이', a: '65세~', b: '55세~' },
        { label: '수령 기간', a: '종신', b: '선택 (10~25년)' },
        { label: '세제 혜택', a: '보험료 소득공제', b: '세액공제 최대 148.5만 원' },
      ],
    },
    relatedLinks: [
      { label: '국민연금 계산기', href: '/pension' },
      { label: 'IRP·ISA 세제혜택 계산기', href: '/tax-benefit' },
    ],
  },
];
